import {MaxPhotoUpload, PlanTypeExtension, PanoramaAllowedPlans, PanoramaMaxRegenCount} from '../constants'

// サービス設定のセレクター
export function selectServiceSettings(state: any) {
    return state.serviceSettings
}

export function selectCanCreateRoomDesign(state: any) {
    const {currentUser, roomDesign} = state
    const {isLoading, beforeImages} = roomDesign
    if (isLoading || beforeImages.length == 0) return false

    const {creditConsumed, creditLimit, isLimitInfinity, account, planType} = currentUser

    // Enterpriseプランの場合のみユーザーのクレジット上限チェック（無制限でない場合のみ）
    if (PlanTypeExtension.isEnterprise(planType)) {
        if (!isLimitInfinity && creditConsumed + beforeImages.length > creditLimit) return false
    }

    // アカウントのクレジット上限チェック
    if (account && account.credit_consumed + beforeImages.length > account.credit_limit) {
        return false
    }

    const nonOKImages = beforeImages.filter((image: any) => !image.roomType || (image.roomType !== 'oneRoom' && !image.lifestyle))
    if (nonOKImages.length > 0) return false

    const hasId = beforeImages.find((image: any) => image.id)
    if (hasId) return false

    return true
}

export function selectCanRegenAfterImages(state: any) {
    const {roomDesign} = state
    const {isLoading} = roomDesign
    if (isLoading) return false

    const selectedBeforeImageData = selectBeforeImageData(state)
    const {id, maxRegenCount} = selectedBeforeImageData
    if (!id) return false
    if (maxRegenCount == 0) return false

    return true
}

export function selectCanChangePageSetting(state: any) {
    const {roomDesign} = state
    const {isLoading, selectedRegenImage} = roomDesign

    if (isLoading) return false

    // 再実行中（selectedRegenImage設定時）は変更不可
    if (selectedRegenImage !== undefined) return false

    return true  // それ以外は変更可能（生成後も変更可能）
}

export function selectCanAddFile(state: any) {
    const {roomDesign} = state
    const {isLoading, beforeImages} = roomDesign
    if (isLoading) return false
    if (beforeImages.length >= MaxPhotoUpload) return false

    const hasId = beforeImages.find((image: any) => image.id)
    if (hasId) return false

    return true
}

export function selectCanReloadRoomDesign(state: any) {
    const {roomDesign} = state
    const {isLoading, beforeImages} = roomDesign
    if (isLoading) return false

    const hasProcessedImage = beforeImages.find((image: any) => image.id)
    return !hasProcessedImage
}

export function selectCanConfirmReloadRoomDesign(state: any) {
    const {roomDesign} = state
    const {isLoading, beforeImages} = roomDesign
    if (isLoading) return false

    const hasProcessedImage = beforeImages.find((image: any) => image.id || image.isGeneratedFail)
    return hasProcessedImage
}

export function selectAfterImageView(state: any) {
    const {roomDesign} = state
    const {isLoading, selectedRegenImage} = roomDesign
    const selectedBeforeImageData = selectBeforeImageData(state)
    const {id, isGeneratedFail} = selectedBeforeImageData ? selectedBeforeImageData : {}
    const {selectedAfterImages, selectedAfterImageData} = selectAfterImageData(state)
    if (isLoading && (!selectedRegenImage || selectedBeforeImageData.stubId == selectedRegenImage) && selectedAfterImages.length == 0)
        return 'show_loading'

    if (isGeneratedFail && (!selectedRegenImage || selectedBeforeImageData.stubId != selectedRegenImage) && selectedAfterImages.length == 0)
        return 'show_gen_image_error'

    if (selectedAfterImageData)
        return 'show_images'

    return 'show_blank'
}

export function selectBeforeImageData(state: any) {
    const {roomDesign} = state
    const {selectedBeforeImage, beforeImages} = roomDesign
    const existBeforeImageData = beforeImages.find((image: any) => image.stubId == selectedBeforeImage)
    const selectedBeforeImageData = existBeforeImageData ? existBeforeImageData : beforeImages[0]
    return selectedBeforeImageData ? selectedBeforeImageData : {}
}

export function selectAfterImageData(state: any) {
    const {roomDesign} = state
    const {afterImages, selectedAfterImage} = roomDesign
    const selectedBeforeImageData = selectBeforeImageData(state)
    const selectedAfterImages = afterImages && selectedBeforeImageData
        ? afterImages.filter((image: any) => image.before_image_id == selectedBeforeImageData.id) : []
    const findAfterImageData = selectedAfterImages.find((image: any) => image.id == selectedAfterImage)
    const selectedAfterImageData = findAfterImageData ? findAfterImageData : selectedAfterImages[0]
    const selectedAfterImageIndex = selectedAfterImages.findIndex((image: any) => image.id == selectedAfterImageData.id)
    return {selectedAfterImages, selectedAfterImageData, selectedAfterImageIndex}
}

// 家具除去機能用のセレクター
export function selectCanProcessItemRemoval(state: any) {
    const {currentUser, itemRemoval} = state;
    const {isLoading, itemImages} = itemRemoval;
    if (isLoading || itemImages.length == 0) return false;

    const {creditConsumed, creditLimit, isLimitInfinity, account, planType} = currentUser;

    // Enterpriseプランの場合のみユーザーのクレジット上限チェック（無制限でない場合のみ）
    if (PlanTypeExtension.isEnterprise(planType)) {
        if (!isLimitInfinity && creditConsumed + itemImages.length > creditLimit) return false;
    }

    // アカウントのクレジット上限チェック（credit_limit = 0 の場合も含めてチェック）
    if (account && account.credit_consumed + itemImages.length > account.credit_limit) {
        return false;
    }

    const hasId = itemImages.find((image: any) => image.id);
    if (hasId) return false;

    return true;
}

export function selectItemImageData(state: any) {
    const {itemRemoval} = state;
    const {itemImages, selectedItemImage} = itemRemoval;
    if (!selectedItemImage) {
        if (itemImages.length == 0) return null;
        return itemImages[0];
    }
    const image = itemImages.find((image: any) => image.stubId == selectedItemImage);
    if (!image) {
        if (itemImages.length == 0) return null;
        return itemImages[0];
    }
    return image;
}

// 家具除去機能用のセレクター - 処理済み画像データの取得
export function selectProcessedImageData(state: any) {
    const {itemRemoval} = state;
    const {afterImages, selectedAfterImage, selectedItemImage} = itemRemoval;
    const selectedItemImageData = selectItemImageData(state);

    if (!selectedItemImageData) return {
        selectedProcessedImages: [],
        selectedProcessedImageData: null,
        selectedProcessedImageIndex: -1
    };

    // 処理済み画像のフィルタリング - stubIdのみで照合（並列処理の競合を回避）
    const selectedProcessedImages = afterImages.filter((image: any) => {
        // stubIdによる明確な紐付け（フロントエンドで一意に管理）
        return selectedItemImageData.stubId === image.stubId;
    });

    if (selectedProcessedImages.length === 0) {
        return {
            selectedProcessedImages: [],
            selectedProcessedImageData: null,
            selectedProcessedImageIndex: -1
        };
    }

    // selectedAfterImageがある場合はそのIDを持つ画像を探す
    let selectedProcessedImageData = null;
    let selectedProcessedImageIndex = -1;

    if (selectedAfterImage) {
        selectedProcessedImageData = selectedProcessedImages.find(
            (image: any) => image.id === selectedAfterImage
        );
        if (selectedProcessedImageData) {
            selectedProcessedImageIndex = selectedProcessedImages.findIndex(
                (image: any) => image.id === selectedAfterImage
            );
        }
    }

    // 見つからなかった場合は最初の画像を使用
    if (!selectedProcessedImageData) {
        selectedProcessedImageData = selectedProcessedImages[0];
        selectedProcessedImageIndex = 0;
    }

    return {
        selectedProcessedImages,
        selectedProcessedImageData,
        selectedProcessedImageIndex
    };
}

// 家具除去機能用のセレクター - 再生成ボタンの有効/無効状態
export function selectCanRegenProcessedImages(state: any) {
    const {itemRemoval} = state;
    const {isLoading} = itemRemoval;
    if (isLoading) return false;

    const selectedItemImageData = selectItemImageData(state);
    if (!selectedItemImageData) return false;

    const {id, maxRegenCount} = selectedItemImageData;
    if (!id) return false;
    if (!maxRegenCount || maxRegenCount <= 0) return false;

    return true;
}

// 家具除去機能用のセレクター - アフターイメージビュー
export function selectItemRemovalAfterImageView(state: any) {
    const {itemRemoval} = state;
    const {isLoading, selectedRegenImage} = itemRemoval;
    const selectedItemImageData = selectItemImageData(state);
    const {selectedProcessedImages, selectedProcessedImageData} = selectProcessedImageData(state);

    // 処理失敗の場合（最優先でチェック）
    if (selectedItemImageData && selectedItemImageData.isProcessedFail) {
        return 'show_gen_image_error';
    }

    // 処理中の場合
    if (isLoading) {
        // 選択画像がある場合
        if (selectedItemImageData) {
            // 再生成処理中の場合
            if (selectedRegenImage && selectedItemImageData.stubId === selectedRegenImage) {
                return 'show_loading';
            }

            // 新規処理中の場合、処理済みでない画像のみロード画面を表示
            if (!selectedItemImageData.isProcessed) {
                return 'show_loading';
            }

            // 処理済み画像の場合は通常表示
            if (selectedItemImageData.isProcessed && selectedProcessedImageData) {
                return 'show_images';
            }
        } else {
            // 選択中の画像がなければロード画面
            return 'show_loading';
        }
    }

    // 選択中の画像が処理済みで、対応する表示用画像がある場合
    if (selectedItemImageData && selectedItemImageData.isProcessed && selectedProcessedImageData) {
        return 'show_images';
    }

    return 'show_blank';
}

// 家具除去機能用のセレクター - 画像生成処理中かどうか
export function selectIsItemRemovalProcessing(state: any) {
    const {itemRemoval} = state;
    const {isLoading, selectedRegenImage} = itemRemoval;
    const selectedItemImageData = selectItemImageData(state);
    const {selectedProcessedImages} = selectProcessedImageData(state);

    // AI家具消し実行時または再生成処理中の場合
    if (isLoading) {
        // AI家具消し実行時
        if (!selectedRegenImage) {
            return true;
        }
        // 再生成処理中
        if (selectedItemImageData && selectedRegenImage && selectedItemImageData.stubId === selectedRegenImage) {
            return true;
        }
    }

    return false;
}

// AIホームステージングの画像処理中かどうか
export function selectIsRoomDesignProcessing(state: any) {
    const {roomDesign} = state;
    const {isLoading, selectedRegenImage} = roomDesign;
    const selectedBeforeImageData = selectBeforeImageData(state);
    const {selectedAfterImages} = selectAfterImageData(state);

    // AIホームステージング実行時または再生成処理中の場合
    if (isLoading) {
        // AIホームステージング実行時
        if (!selectedRegenImage) {
            return true;
        }
        // 再生成処理中
        if (selectedBeforeImageData && selectedRegenImage && selectedBeforeImageData.stubId === selectedRegenImage) {
            return true;
        }
    }

    return false;
}

// 家具除去機能用のセレクター - 画像を追加できるかどうか
export function selectCanAddFileItemRemoval(state: any) {
    const {itemRemoval} = state;
    const {isLoading, itemImages} = itemRemoval;

    // 処理中は追加不可
    if (isLoading) return false;

    // 最大枚数に達している場合は追加不可
    if (itemImages.length >= MaxPhotoUpload) return false;

    // 処理済み画像（IDがある）がある場合は追加不可
    const hasId = itemImages.find((image: any) => image.id);
    if (hasId) return false;

    return true;
}

// 画像編集機能用のセレクター - 画像編集処理中かどうか
export function selectIsImageEditingProcessing(state: any) {
    const {imageEditing} = state;
    const {isProcessing} = imageEditing;
    return isProcessing;
}

// 間取り抜き出し機能用のセレクター - 処理実行可否
export function selectCanProcessFloorPlan(state: any) {
    const {currentUser, floorPlanExtraction} = state;
    const {isLoading, sourceImage, selectionPreviewBlob} = floorPlanExtraction;

    // 処理中は不可
    if (isLoading) return false;

    // 画像と選択範囲が必要
    if (!sourceImage || !selectionPreviewBlob) return false;

    // クレジットチェック
    const {creditConsumed, creditLimit, isLimitInfinity, account, planType} = currentUser;

    // Enterpriseプランの場合のみユーザーのクレジット上限チェック
    if (PlanTypeExtension.isEnterprise(planType)) {
        if (!isLimitInfinity && creditConsumed >= creditLimit) return false;
    }

    // アカウントのクレジット上限チェック
    if (account && account.credit_consumed >= account.credit_limit) {
        return false;
    }

    return true;
}

// 間取り抜き出し機能用のセレクター - 処理中かどうか
export function selectIsFloorPlanProcessing(state: any) {
    const {floorPlanExtraction} = state;
    const {isLoading} = floorPlanExtraction;
    return isLoading;
}

// 間取り抜き出し機能用のセレクター - 表示ビュー状態
export function selectFloorPlanAfterImageView(state: any) {
    const {floorPlanExtraction} = state;
    const {isLoading, sourceImage, afterImages} = floorPlanExtraction;

    if (isLoading) return 'show_loading';
    if (afterImages.length > 0) return 'show_images';
    if (sourceImage) return 'show_selection';
    return 'show_blank';
}

// 間取り抜き出し機能用のセレクター - 選択中の結果画像
export function selectFloorPlanAfterImageData(state: any) {
    const {floorPlanExtraction} = state;
    const {afterImages, selectedAfterImage} = floorPlanExtraction;

    if (afterImages.length === 0) return null;

    if (selectedAfterImage) {
        const found = afterImages.find((img: any) => img.id === selectedAfterImage);
        if (found) return found;
    }

    return afterImages[afterImages.length - 1];
}

// パノラマステージング機能用のセレクター

// 選択中のパノラマbeforeImage
export function selectPanoramaSelectedBeforeImage(state: any) {
    const {panorama} = state;
    const {beforeImages, selectedBeforeImageStubId} = panorama;

    if (!selectedBeforeImageStubId || beforeImages.length === 0) return null;
    return beforeImages.find((img: any) => img.stubId === selectedBeforeImageStubId) || null;
}

// パノラマ処理実行可否
export function selectCanProcessPanorama(state: any) {
    const {currentUser, panorama} = state;
    const {isLoading, beforeImages} = panorama;

    if (isLoading) return false;
    if (!beforeImages || beforeImages.length === 0) return false;

    // 未処理の画像の枚数をカウント
    const unprocessedImages = beforeImages.filter((img: any) => !img.id);
    if (unprocessedImages.length === 0) return false;

    const {creditConsumed, creditLimit, isLimitInfinity, account, planType} = currentUser;

    // プラン制限チェック
    if (!PanoramaAllowedPlans.includes(planType)) return false;

    // Enterpriseプランの場合のみユーザーのクレジット上限チェック（処理枚数を考慮）
    if (PlanTypeExtension.isEnterprise(planType)) {
        if (!isLimitInfinity && creditConsumed + unprocessedImages.length > creditLimit) return false;
    }

    // アカウントのクレジット上限チェック（処理枚数を考慮）
    if (account && account.credit_consumed + unprocessedImages.length > account.credit_limit) {
        return false;
    }

    return true;
}

// パノラマ処理中かどうか
export function selectIsPanoramaProcessing(state: any) {
    const {panorama} = state;
    return panorama.isLoading;
}

// パノラマ結果画像の表示状態
export function selectPanoramaAfterImageView(state: any) {
    const {panorama} = state;
    const {isLoading, errorMessage} = panorama;
    const selectedBeforeImage = selectPanoramaSelectedBeforeImage(state);

    // 生成済み画像があれば表示（エラーがあっても画像を優先表示）
    if (selectedBeforeImage?.afterImages && selectedBeforeImage.afterImages.length > 0) return 'show_images';
    // ローディング中ならローディング表示（エラー表示より優先）
    if (isLoading) return 'show_loading';
    // 画像がない場合はエラーをチェック
    if (selectedBeforeImage?.errorMessage) return 'show_error';
    if (errorMessage) return 'show_error';
    if (selectedBeforeImage) return 'show_blank';
    return 'show_empty';
}

// 再生成可否
export function selectCanRegenPanorama(state: any) {
    const {panorama} = state;
    const {isLoading} = panorama;
    const selectedBeforeImage = selectPanoramaSelectedBeforeImage(state);

    if (isLoading) return false;
    if (!selectedBeforeImage || !selectedBeforeImage.id) return false;
    if ((selectedBeforeImage.regenerationCount || 0) >= PanoramaMaxRegenCount) return false;

    return true;
}

// 選択中の結果画像データ
export function selectPanoramaAfterImageData(state: any) {
    const {panorama} = state;
    const selectedBeforeImage = selectPanoramaSelectedBeforeImage(state);
    const afterImages = selectedBeforeImage?.afterImages || [];
    // グローバルのselectedAfterImageIdを優先して使用
    const selectedAfterImageId = panorama.selectedAfterImageId ?? selectedBeforeImage?.selectedAfterImageId;

    if (afterImages.length === 0) return null;

    if (selectedAfterImageId) {
        const found = afterImages.find((img: any) => img.id === selectedAfterImageId);
        if (found) return found;
    }

    return afterImages[0];
}

// プラン制限チェック
export function selectIsPanoramaAllowedPlan(state: any) {
    const {currentUser} = state;
    const {planType} = currentUser;
    return PanoramaAllowedPlans.includes(planType);
}

// パノラマタブが利用可能かどうか（プラン制限とクレジット上限チェック）
export function selectIsPanoramaTabDisabled(state: any) {
    const {currentUser} = state;
    const {creditConsumed, creditLimit, isLimitInfinity, account, planType} = currentUser;

    // プラン制限チェック
    if (!PanoramaAllowedPlans.includes(planType)) return true;

    // Enterpriseプランの場合のみユーザーのクレジット上限チェック
    if (PlanTypeExtension.isEnterprise(planType)) {
        if (!isLimitInfinity && creditConsumed >= creditLimit) return true;
    }

    // アカウントのクレジット上限チェック
    if (account && account.credit_consumed >= account.credit_limit) {
        return true;
    }

    return false;
}
